import { SearchOutlined, SettingOutlined, CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Dropdown,
  Input,
  Space,
  Spin,
  Table,
  Tooltip
} from "antd";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RWebShare } from "react-web-share";
import { getRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";
import partnerDeleteService from "../../api/partnerDeleteService";
import cardViewSelected from "../../assets/images/card-view-selected.svg";
import cardView from "../../assets/images/card-view.svg";
import CopyFilled from "../../assets/images/copyFilled.svg";
import CopyOutlined from "../../assets/images/copyOutLined.svg";
import deleteIcon from "../../assets/images/delete-icon.svg";
import editIcon from "../../assets/images/edit-icon.svg";
import listViewSelected from "../../assets/images/list-view-selected.svg";
import listView from "../../assets/images/list-view.svg";
import PlusIcon from "../../assets/images/plus-icon.svg";
import shareicon from "../../assets/images/share-icon.svg";
import shareIcon from "../../assets/images/share.svg";
import { inputFieldCharLimit } from '../../config/index';
import { SecureDeleteFlow } from "../secure-delete";
import Line from "../line/index";
import "./partners.scss";
import ProSettings from "./ProSettings";
import { truncate } from "../../utils/helper";
import { isSuperAdmin } from "../../utils/auth";
import { debounce } from 'lodash';
import { useStore } from "../../store";
import { setActivePartner } from "../../store/actions";

const sanitizeSearchInput = (input) => {
  if (input === undefined || input === null) return '';
  if (typeof input !== 'string') return '';

  const sanitized = input.replace(/[^a-zA-Z0-9\s\-_]/g, '');

  console.log('🔍 Search Sanitization - Input:', `"${input}"`, 'Output:', `"${sanitized}"`);
  return sanitized;
};

const getSafeSearchParam = (input) => {
  if (!input || input.trim() === '') return '';

  const sanitized = sanitizeSearchInput(input.trim());
  if (!sanitized) return '';

  return encodeURIComponent(sanitized);
};

const PartnerList = ({ reload }) => {
  const navigate = useNavigate();
  const { dispatch } = useStore();
  const [partners, setPartners] = useState([]);

  const [searchTerm, setSearchTerm] = useState('')
  const [boolaf, setBoolAf] = useState(false);
  const [boolgl, setBoolGl] = useState(false);
  const [boolms, setBoolMs] = useState(false);
  const [booltz, setBoolTz] = useState(false);
  const [af, setAf] = useState("");
  const [gl, setGl] = useState("");
  const [ms, setMs] = useState("");
  const [tz, setTz] = useState("");

  const [loading, setLoading] = useState(true);
  const [copiedPartner, setCopiedPartner] = useState(null);

  const [deletePartnerId, setDeletePartnerId] = useState("");
  const [deletePartnerName, setDeletePartnerName] = useState("");
  const [deletePartnerModal, setDeletePartnerModal] = useState(false);
  const [viewSelected, setViewSelected] = useState("card");

  const [proSettingsModal, setProSettingsModal] = useState(false)
  const [proSettingsModalData, setProSettingsModalData] = useState()

  const handleAF = () => {
    if (!boolaf) {
      setAf("A-F");
      setBoolAf(true);
    } else {
      setAf("");
      setBoolAf(false);
    }
  };

  const handleGL = () => {
    if (!boolgl) {
      setGl("G-L");
      setBoolGl(true);
    } else {
      setGl("");
      setBoolGl(false);
    }
  };

  const handleMS = () => {
    if (!boolms) {
      setMs("M-S");
      setBoolMs(true);
    } else {
      setMs("");
      setBoolMs(false);
    }
  };

  const handleTZ = () => {
    if (!booltz) {
      setTz("T-Z");
      setBoolTz(true);
    } else {
      setTz("");
      setBoolTz(false);
    }
  };

  const fetchData = useCallback(async (searchValue = null) => {
    try {
      setLoading(true);

      let url;
      const currentSearchTerm = searchValue ?? searchTerm;

      if (currentSearchTerm && currentSearchTerm.trim() !== '') {
        const safeSearchParam = getSafeSearchParam(currentSearchTerm);
        if (!safeSearchParam) {
          // If search term becomes empty after sanitization, get all data
          url = userServiceApi.getPartners(boolaf, boolgl, boolms, booltz, af, gl, ms, tz);
        } else {
          // Use the search endpoint with safe parameter
          url = userServiceApi.searchPartner(safeSearchParam);
        }
      } else {
        url = userServiceApi.getPartners(boolaf, boolgl, boolms, booltz, af, gl, ms, tz);
      }

      console.log('🌐 API URL:', url);

      const response = await getRequest(url);
      if (response) {
        setPartners(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching partner data:", error);
      toast.error("Failed to load partner data");
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, boolaf, boolgl, boolms, booltz, af, gl, ms, tz]);

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        fetchData(value);
      }, 600),
    [fetchData]
  );

  // Search change handler
  const handleSearchChange = (e) => {
    const rawValue = e.target.value || '';
    const sanitizedValue = sanitizeSearchInput(rawValue);

    setSearchTerm(sanitizedValue);

    if (sanitizedValue.trim().length > 0) {
      debouncedSearch(sanitizedValue);
    } else {
      // If search is cleared, get all data
      fetchData('');
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    fetchData('');
  };

  // Handle enter key press
  const handlePressEnter = (e) => {
    const rawValue = e.target.value || '';
    const sanitizedValue = sanitizeSearchInput(rawValue);

    if (sanitizedValue.trim().length > 0) {
      fetchData(sanitizedValue);
    }
  };

  useEffect(() => {
    setSearchTerm('')
    fetchData('');
    // Search input is handled separately; including fetchData here would reset the query on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, af, gl, ms, tz]);

  const canDeletePartner = isSuperAdmin();

  const handleDelete = (partner) => {
    if (!canDeletePartner) {
      toast.error("You are not authorized to delete partners.");
      return;
    }
    setDeletePartnerId(partner.id);
    setDeletePartnerName(partner.name);
    setDeletePartnerModal(true);
  };

  const closeDeleteModal = () => {
    setDeletePartnerId("");
    setDeletePartnerName("");
    setDeletePartnerModal(false);
  };

  const handleSecureDeleteSuccess = () => {
    fetchData();
  };

  const handleAddNewPartnerClick = () => {
    navigate("/add-partner");
  };

  const activatePartner = (partner) => {
    if (!partner?.id) return;
    const mediaLink = partner.mediaLink || null;
    dispatch(setActivePartner({ id: partner.id, mediaLink }));
    sessionStorage.setItem("partnerId", partner.id);
    if (mediaLink) {
      sessionStorage.setItem("partnerMediaLink", mediaLink);
    } else {
      sessionStorage.removeItem("partnerMediaLink");
    }
  };

  const handleEditClick = (partner) => {
    if (partner) {
      activatePartner(partner);
      navigate(`/edit-partner/${partner.id}`, {
        state: { selectedPartner: partner },
      });
    }
  };

  const handleViewDashboard = (partner) => {
    activatePartner(partner);
    sessionStorage.setItem("partnerName", partner.name);
    navigate(`/dashboard/${partner.id}`);
  };

  const handleConfigurationClick = (partner) => {
    activatePartner(partner);
    sessionStorage.setItem("partnerName", partner.name);
    sessionStorage.setItem("subDomainName", partner?.subDomainName ?? null);
    navigate(`/configuration/${partner.id}`);
  };

  const handleViewOrganizations = (partner) => {
    activatePartner(partner);
    navigate(`/organisation-list/${partner.id}`);
  };

  const copyToClipboard = async (url, type, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedPartner({ id, type });
      toast.success("URL copied to clipboard");
    } catch (error) {
      console.error(error);
      toast.error("Failed to copy URL");
    }
  };

  const columns = [
    {
      title: "Partner Details",
      dataIndex: "mediaLink",
      key: "mediaLink",
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img src={record.mediaLink} style={{ height: 60, maxWidth: 100 }} alt="Partner logo" />
        </div>
      ),
    },
    {
      title: " ",
      dataIndex: "name",
      key: "name",
      render: (text) => <div
        style={{ fontSize: "15px", overflow: 'hidden', whiteSpace: "nowrap", textOverflow: 'ellipsis', maxWidth: '300px' }}>
        {text}
      </div>,
    },
    {
      title: "Site Name",
      dataIndex: "siteName",
      key: "siteName",
      render: (text) => <div
        style={{ fontSize: "15px", overflow: 'hidden', whiteSpace: "nowrap", textOverflow: 'ellipsis', maxWidth: '300px' }}>
        {text}
      </div>,
    },
    {
      title: " ",
      key: "action",
      render: (text, record) => (
        <Space size="middle" style={{ height: 60 }}>
          <div
            className="navigate-buttons"
            onClick={() => handleViewDashboard(record)}
          >
            Dashboards
          </div>
          <div
            className="navigate-buttons"
            onClick={() => handleViewOrganizations(record)}
          >
            Organisations
          </div>
          <div
            className="navigate-buttons last-navigate-button"
            onClick={() => handleConfigurationClick(record)}
          >
            Configurations
          </div>
          {canDeletePartner ? (
            <>
              <img
                src={deleteIcon}
                alt="deleteIcon"
                onClick={() => handleDelete(record)}
                style={{ cursor: "pointer" }}
              />
              <div className="line" />
            </>
          ) : null}
          <img
            src={editIcon}
            alt="editIcon"
            onClick={() => handleEditClick(record)}
            style={{ cursor: "pointer" }}
          />
          <div className="line" />
          <SettingOutlined onClick={() => {
            setProSettingsModal(true)
            setProSettingsModalData(record)
            fetchAllProSettings(record)
          }}
            style={{
              fontSize: '19px',
              color: '#3067ae',
              cursor: 'pointer'
            }} />
          <div className="line" />
          <div className="kebab-menu">
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              overlay={
                <div
                  style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: "5px",
                    backgroundColor: "#fff",
                  }}
                >
                  <div
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #f0f0f0",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      className="share-dropdown"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <RWebShare
                        data={{
                          url: `${record?.endUserUrl}`,
                        }}
                      >
                        <span>
                          <img src={shareIcon} alt="share" />
                          <span style={{ marginLeft: "5px" }}>
                            website URL
                          </span>
                        </span>
                      </RWebShare>
                      {copiedPartner?.id === record.id && copiedPartner?.type === "endUser" ? (
                        <div style={{ marginLeft: "20px" }}>
                          <img src={CopyFilled} alt="copyFilled" />
                        </div>
                      ) : (
                        <div
                          style={{ marginLeft: "20px" }}
                          onClick={() => copyToClipboard(record?.endUserUrl, "endUser", record.id)}
                        >
                          <img src={CopyOutlined} alt="copyOutlined" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #f0f0f0",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      className="share-dropdown"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <RWebShare
                        data={{
                          url: `${record?.orgUrl}`,
                        }}
                      >
                        <span>
                          <img src={shareIcon} alt="share" />
                          <span style={{ marginLeft: "5px" }}>
                            organisation URL
                          </span>
                        </span>
                      </RWebShare>
                      {copiedPartner?.id === record.id && copiedPartner?.type === "org" ? (
                        <div style={{ marginLeft: "20px" }}>
                          <img src={CopyFilled} alt="copyFilled" />
                        </div>
                      ) : (
                        <div
                          style={{ marginLeft: "20px" }}
                          onClick={() => copyToClipboard(record?.orgUrl, "org", record.id)}
                        >
                          <img src={CopyOutlined} alt="copyOutlined" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #f0f0f0",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      className="share-dropdown"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <RWebShare
                        data={{
                          url: `${record?.partnerUrl}`,
                        }}
                      >
                        <span>
                          <img src={shareIcon} alt="share" />
                          <span style={{ marginLeft: "5px" }}>
                            partner URL
                          </span>
                        </span>
                      </RWebShare>

                      {copiedPartner?.id === record.id && copiedPartner?.type === "partner" ? (
                        <div style={{ marginLeft: "20px" }}>
                          <img src={CopyFilled} alt="copyFilled" />
                        </div>
                      ) : (
                        <div
                          style={{ marginLeft: "20px" }}
                          onClick={() => copyToClipboard(record?.partnerUrl, "partner", record.id)}
                        >
                          <img src={CopyOutlined} alt="copyOutlined" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              }
            >
              <img
                src={shareicon}
                alt="Options"
                style={{ cursor: "pointer", marginTop: "5px" }}
              />
            </Dropdown>
          </div>
        </Space>
      ),
    },
  ];

  const [allProSettingsData, setAllProSettingsData] = useState(null)

  const fetchAllProSettings = async (partner) => {
    console.log('partner', partner)
    try {
      const res = await getRequest(userServiceApi.getAllProSettings(partner.id || ""))
      console.log('res', res)
      if (res) {
        console.log('data', res.data)
        setAllProSettingsData(res.data)
      }
    } catch (error) {
      console.error("Error fetching Site Features:", error);
    }
  }

  useEffect(() => {
    if (copiedPartner) {
      const timer = setTimeout(() => {
        setCopiedPartner(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [copiedPartner]);

  return (
    <div>
      <div className="main-container">
        <div className="filters-container">
          <div className="partner-heading">Partners</div>
          <div className="buttons">
            <div className="searchbar-containers">
              <Tooltip placement="top" title='Allowed: letters, numbers, spaces, hyphens (-), and underscores (_).'>
                <Input
                  className="searchbar"
                  placeholder="Search Partners"
                  suffix={
                    searchTerm ? (
                      <CloseOutlined
                        style={{ color: "#CC0E2D", verticalAlign: "middle", cursor: 'pointer' }}
                        onClick={handleClearSearch}
                      />
                    ) : (
                      <SearchOutlined
                        style={{ color: "#CC0E2D", verticalAlign: "middle" }}
                      />
                    )
                  }
                  onChange={handleSearchChange}
                  onPressEnter={handlePressEnter}
                  value={searchTerm}
                  maxLength={inputFieldCharLimit}
                />
              </Tooltip>
            </div>
            <Line />
            <div className="alphabets-filter">
              <Button
                id="A-F-btn"
                className={`${!boolaf
                  ? "alpha-filter-btn first-btn"
                  : "alpha-filter-btn-select first-btn"
                  }`}
                onClick={handleAF}
              >
                <span className="alphas">A-F</span>
              </Button>
              <Button
                id="G-L-btn"
                className={`${!boolgl ? "alpha-filter-btn" : "alpha-filter-btn-select"
                  }`}
                onClick={handleGL}
              >
                <span className="alphas">G-L</span>
              </Button>
              <Button
                id="M-S-btn"
                className={`${!boolms ? "alpha-filter-btn" : "alpha-filter-btn-select"
                  }`}
                onClick={handleMS}
              >
                <span className="alphas">M-S</span>
              </Button>
              <Button
                id="T-Z-btn"
                className={`${!booltz ? "alpha-filter-btn" : "alpha-filter-btn-select"
                  }`}
                onClick={handleTZ}
              >
                <span className="alphas">T-Z</span>
              </Button>
            </div>
            <Button
              className="add-partner-btn"
              onClick={handleAddNewPartnerClick}
            >
              <img
                src={PlusIcon}
                alt="plusIcon"
                style={{ marginRight: "5px" }}
              />
              Add Partner
            </Button>
            <Line />
            {viewSelected === "list" ? (
              <img
                src={listViewSelected}
                alt="listviewSelected"
                style={{ margin: "0px 10px 0px 10px" }}
              />
            ) : (
              <img
                src={listView}
                alt="listview"
                style={{ cursor: "pointer", margin: "0px 10px 0px 10px" }}
                onClick={() => setViewSelected("list")}
              />
            )}
            <Line />
            {viewSelected === "card" ? (
              <img
                src={cardViewSelected}
                alt="cardViewSelected"
                style={{ marginLeft: "10px" }}
              />
            ) : (
              <img
                src={cardView}
                alt="cardView"
                style={{ cursor: "pointer", marginLeft: "10px" }}
                onClick={() => setViewSelected("card")}
              />
            )}
          </div>
        </div>
        {loading ? (
          <Spin
            className="data-loader loading-svg"
            tip="Loading"
            size="large"
          />
        ) : viewSelected === "list" ? (
          <div className="table-view-deleted">
            {partners?.length > 0 ? (
              <Table
                rowClassName={() => "rowClassName1"}
                dataSource={partners}
                columns={columns}
                rowKey="id"
                scroll={{ x: "max-content" }}   // ✅ Important
                locale={{
                  emptyText: (
                    <div style={{ width: "100%", textAlign: "center" }}>
                      No partners available
                    </div>
                  ),
                }}
              />
            ) : (
              <h3 className="placeholder" style={{ textAlign: "center", margin: "50px 0" }}>
                {searchTerm ? `No results found for "${searchTerm}"` : "No partners available"}
              </h3>
            )}

          </div>
        ) : (
          <div
            className="partner-list-container"
            style={partners?.length === 0 ? { display: 'block' } : null}
          >

            {partners?.length > 0 ? (
              partners?.map((partner) => (
                <Card className="partner-card" key={partner.id} size="small">
                  <div className="card-header-container">
                    <div className="card-heading">{truncate(partner.name, 25)}</div>
                    <div className="icons-stack">
                      {canDeletePartner ? (
                        <>
                          <img
                            src={deleteIcon}
                            alt="deleteIcon"
                            style={{ marginRight: "5px", cursor: "pointer" }}
                            onClick={() => handleDelete(partner)}
                          />
                          <Line />
                        </>
                      ) : null}
                      <img
                        src={editIcon}
                        alt="editIcon"
                        style={{ margin: "0px 5px 0px 5px", cursor: "pointer" }}
                        onClick={() => handleEditClick(partner)}
                      />
                      <Line />
                      <SettingOutlined onClick={() => {
                        setProSettingsModal(true)
                        setProSettingsModalData(partner)
                        fetchAllProSettings(partner)
                      }}
                        style={{
                          fontSize: '19px',
                          color: '#3067ae',
                          cursor: 'pointer'
                        }} />
                      <Line />
                      <div className="kebab-menu">
                        <Dropdown
                          trigger={["click"]}
                          placement="bottomRight"
                          overlay={
                            <div
                              style={{
                                border: "1px solid #f0f0f0",
                                borderRadius: "5px",
                                backgroundColor: "#fff",
                              }}
                            >
                              <div
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #f0f0f0",
                                  cursor: "pointer",
                                }}
                              >
                                <div
                                  className="share-dropdown"
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <RWebShare
                                    data={{
                                      url: `${partner?.endUserUrl}`,
                                    }}
                                  >
                                    <span>
                                      <img src={shareIcon} alt="share" />
                                      <span style={{ marginLeft: "5px" }}>
                                        website URL
                                      </span>
                                    </span>
                                  </RWebShare>
                                  {copiedPartner?.id === partner.id && copiedPartner?.type === "endUser" ? (
                                    <div style={{ marginLeft: "20px" }}>
                                      <img src={CopyFilled} alt="copyFilled" />
                                    </div>
                                  ) : (
                                    <div
                                      style={{ marginLeft: "20px" }}
                                      onClick={() => copyToClipboard(partner?.endUserUrl, "endUser", partner.id)}
                                    >
                                      <img src={CopyOutlined} alt="copyOutlined" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #f0f0f0",
                                  cursor: "pointer",
                                }}
                              >
                                <div
                                  className="share-dropdown"
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <RWebShare
                                    data={{
                                      url: `${partner?.orgUrl}`,
                                    }}
                                  >
                                    <span>
                                      <img src={shareIcon} alt="share" />
                                      <span style={{ marginLeft: "5px" }}>
                                        organisation URL
                                      </span>
                                    </span>
                                  </RWebShare>

                                  {copiedPartner?.id === partner.id && copiedPartner?.type === "org" ? (
                                    <div style={{ marginLeft: "20px" }}>
                                      <img src={CopyFilled} alt="copyFilled" />
                                    </div>
                                  ) : (
                                    <div
                                      style={{ marginLeft: "20px" }}
                                      onClick={() => copyToClipboard(partner?.orgUrl, "org", partner.id)}
                                    >
                                      <img src={CopyOutlined} alt="copyOutlined" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #f0f0f0",
                                  cursor: "pointer",
                                }}
                              >
                                <div
                                  className="share-dropdown"
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <RWebShare
                                    data={{
                                      url: `${partner?.partnerUrl}`,
                                    }}
                                  >
                                    <span>
                                      <img src={shareIcon} alt="share" />
                                      <span style={{ marginLeft: "5px" }}>
                                        partner URL
                                      </span>
                                    </span>
                                  </RWebShare>
                                  
                                  {copiedPartner?.id === partner.id && copiedPartner?.type === "partner" ? (
                                    <div style={{ marginLeft: "20px" }}>
                                      <img src={CopyFilled} alt="copyFilled" />
                                    </div>
                                  ) : (
                                    <div
                                      style={{ marginLeft: "20px" }}
                                      onClick={() => copyToClipboard(partner?.partnerUrl, "partner", partner.id)}
                                    >
                                      <img src={CopyOutlined} alt="copyOutlined" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          }
                        >
                          <img
                            src={shareicon}
                            alt="Options"
                            style={{ cursor: "pointer", marginTop: "5px" }}
                          />
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                  <div className="card-main-container">
                    <div className="partner-logo-container">
                      <img src={partner.mediaLink} alt={partner.name} />
                      <div className="site-name">{truncate(partner.siteName, 20)}</div>
                    </div>
                    <div className="partner-configurations-container">
                      <div
                        className="configure-btn"
                        onClick={() => handleViewDashboard(partner)}
                      >
                        <p>Dashboards</p>
                      </div>
                      <Divider className="partner-card-line" type="vertical" />
                      <div
                        className="configure-btn"
                        onClick={() => handleViewOrganizations(partner)}
                      >
                        <p>Organisations</p>
                      </div>
                      <Divider className="partner-card-line" type="vertical" />
                      <div
                        className="configure-btn"
                        onClick={() => handleConfigurationClick(partner)}
                      >
                        <p>Configurations</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="no-data-message">
                <h3 style={{ textAlign: "center", margin: "50px 0" }}>
                  {searchTerm ? `No results found for "${searchTerm}"` : "No partners available"}
                </h3>
              </div>
            )}
          </div>
        )}
      </div>
      {allProSettingsData && <ProSettings isModalOpen={proSettingsModal}
        setIsModalOpen={setProSettingsModal}
        proSettingsModalData={proSettingsModalData}
        setProSettingsModalData={setProSettingsModalData}
        fetchData={fetchData}
        allProSettingsData={allProSettingsData}
        setAllProSettingsData={setAllProSettingsData}
      />}
      <SecureDeleteFlow
        open={deletePartnerModal}
        onClose={closeDeleteModal}
        entityType="Partner"
        entityId={deletePartnerId}
        displayName={deletePartnerName}
        requiredRole="SUPER_ADMIN"
        warningMessage="Are you sure you want to delete this Partner? The Partner will be moved to the Restore section, where it can be restored within 30 days."
        subWarningMessage="All organisations under this partner will also be deleted."
        successMessage="Partner deleted successfully."
        sendOtp={() =>
          partnerDeleteService.sendDeleteOtp({
            partnerId: deletePartnerId,
            partnerName: deletePartnerName,
          })
        }
        verifyOtp={(otp) =>
          partnerDeleteService.verifyDeleteOtp({
            partnerId: deletePartnerId,
            otp,
          })
        }
        deleteAction={(verificationResult) =>
          partnerDeleteService.deletePartner({
            partnerId: deletePartnerId,
            verificationToken: verificationResult?.verificationToken,
          })
        }
        onSuccess={handleSecureDeleteSuccess}
      />
    </div>
  );
};

export default PartnerList;